import { Users, User, GraduationCap } from 'lucide-react';
import { Label } from '~/components/ui/label';
import { Switch } from '~/components/ui/switch';
import { Badge } from '~/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { StepHeader } from '~/components/forms/StepHeader';

/**
 * SelectStudentsStep - Select which existing students are returning
 *
 * For returning students, we display their current grade level as read-only.
 * The school admin will set the next grade level after the confirmation is submitted.
 */
export function SelectStudentsStep({ formData, gradeLevels: _gradeLevels = [], updateFormData }: any) {
  const returningStudents = formData?.returningStudents || [];

  const handleStudentToggle = (studentEnrollmentId: string, isReturning: boolean) => {
    const updated = returningStudents.map((student: any) => {
      if (student.studentEnrollmentId === studentEnrollmentId) {
        return {
          ...student,
          isReturning
        };
      }
      return student;
    });
    updateFormData('returningStudents', updated);
  };

  const returningCount = returningStudents.filter((s: any) => s.isReturning !== false).length;

  return (
    <div className="space-y-6">
      <StepHeader
        icon={Users}
        title="Returning Students"
        description="Please confirm which students will be returning next year."
      />

      {returningStudents.length === 0 ? (
        <Card className="max-w-xl mx-auto">
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">
              No existing students found. You can add new students in the next step.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4 max-w-xl mx-auto">
          {returningStudents.map((student: any) => {
            const isReturning = student.isReturning !== false;

            return (
              <Card key={student.studentEnrollmentId} className="overflow-hidden">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                        <User className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div>
                        <CardTitle className="text-base">
                          {student.firstName} {student.lastName}
                        </CardTitle>
                        {student.currentGradeLevel && (
                          <CardDescription>
                            Currently: {student.currentGradeLevel}
                          </CardDescription>
                        )}
                      </div>
                    </div>
                    <Badge variant={isReturning ? 'default' : 'secondary'}>
                      {isReturning ? 'Returning' : 'Not Returning'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label
                      htmlFor={`returning-${student.studentEnrollmentId}`}
                      className="cursor-pointer"
                    >
                      Will return next year
                    </Label>
                    <Switch
                      id={`returning-${student.studentEnrollmentId}`}
                      checked={isReturning}
                      onCheckedChange={(checked) =>
                        handleStudentToggle(student.studentEnrollmentId, checked)
                      }
                    />
                  </div>

                  {isReturning && student.currentGradeLevel && (
                    <div className="pt-2 border-t flex items-center gap-2 text-sm text-muted-foreground">
                      <GraduationCap className="h-4 w-4" />
                      <span>Upcoming grade: <span className="font-medium text-foreground">{student.currentGradeLevel}</span></span>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          <div className="text-center pt-4">
            <p className="text-sm text-muted-foreground">
              {returningCount} of {returningStudents.length} student{returningStudents.length !== 1 ? 's' : ''} returning
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default SelectStudentsStep;
